import {useQuery} from '@tanstack/react-query';
import {HomeCalendarResponse, HomeService} from 'client';
import {addMonths, isSameDay, startOfMonth} from 'date-fns';
import {useAtomValue} from 'jotai';
import {useCallback, useEffect, useMemo, useState} from 'react';
import {getService} from 'utilities';

import {calendarPeopleFilterAtom} from './calendarPeopleFilterAtom';
import {availableCalendarPeopleGroups, filterByCalendarPeopleGroups} from './calendarPeopleGroups';
import {CalendarPeopleGroup} from './calendarPeopleGroups';
import {
  AnniversaryPerson,
  BirthdayPerson,
  buildCalendarDays,
  CalendarDay,
  CalendarView,
  CalendarWindow,
  calendarWindow,
  calendarWindowLabel,
  firstUpcomingOutMonth,
  peopleOutInRange,
  PtoPerson,
  toAnniversaryPeople,
  toBirthdayPeople,
  toPtoPeople,
} from './teamCalendar';
import {WeekStart, weekStartAtom} from './WeekStartToggle';

export interface TeamCalendarData {
  /** The month, or the `ahead` weeks around today, currently shown. */
  visible: CalendarWindow;
  /** Header label for {@link visible}: "October 2026" or "Oct – Nov 2026". */
  label: string;
  days: CalendarDay[];
  overlapLayers: Map<string, number>;
  /** Whether the grid weeks start on Sunday (0) or Monday (1). */
  weekStartsOn: WeekStart;
  /** Everyone with an upcoming PTO span. */
  people: PtoPerson[];
  /** Everyone with a recurring birthday. */
  birthdays: BirthdayPerson[];
  /** Everyone with a recurring work anniversary. */
  anniversaries: AnniversaryPerson[];
  isLoading: boolean;
  goToPrevMonth: () => void;
  goToNextMonth: () => void;
  /** Return to the `ahead` view (last week, then today's row and four more). */
  goToToday: () => void;
  /** True when the grid already matches the go-to-today view (hide that button). */
  isOnToday: boolean;
  /** People cohort groups present in the viewer's data (drives the settings menu). */
  availablePeopleGroups: CalendarPeopleGroup[];
}

/**
 * Drives the Calendar tile's mini calendar: holds the viewed month, pulls the
 * viewer's "my people" birthdays + PTO, and exposes month-tab navigation.
 *
 * Both lists come from a single endpoint and neither is date-ranged —
 * birthdays recur (month/day) and each person has one upcoming PTO span — so we
 * fetch once and tab across months entirely client-side. The tile opens on the
 * `ahead` view (last week, today's row, four more); if nobody is out in it, we jump once
 * to the next month that has someone out.
 */
export const useTeamCalendar = (): TeamCalendarData => {
  const currentMonth = useMemo(() => startOfMonth(new Date()), []);
  const [view, setView] = useState<CalendarView>({kind: 'ahead'});
  // Viewer's saved week-start preference (locale default when unset).
  const weekStartsOn = useAtomValue(weekStartAtom);
  const peopleFilter = useAtomValue(calendarPeopleFilterAtom);
  // Auto-jump to the first non-empty month at most once, and never over a
  // manual navigation.
  const [didAutoJump, setDidAutoJump] = useState(false);

  const {data, isLoading} = useQuery<HomeCalendarResponse>({
    queryKey: ['home', 'people-calendar'],
    queryFn: getService(HomeService).peopleCalendarApiV1HomePeopleCalendarGet,
  });

  const people = useMemo(() => toPtoPeople(data?.pto ?? []), [data?.pto]);
  const birthdays = useMemo(() => toBirthdayPeople(data?.birthdays ?? []), [data?.birthdays]);
  const anniversaries = useMemo(
    () => toAnniversaryPeople(data?.anniversaries ?? []),
    [data?.anniversaries]
  );

  // Cohorts are drawn from every event type shown (PTO + birthdays +
  // anniversaries) so the menu reflects everyone on the calendar.
  const availablePeopleGroups = useMemo(
    () => availableCalendarPeopleGroups([...people, ...birthdays, ...anniversaries]),
    [people, birthdays, anniversaries]
  );

  const filteredPeople = useMemo(
    () => filterByCalendarPeopleGroups(people, peopleFilter, availablePeopleGroups),
    [people, peopleFilter, availablePeopleGroups]
  );
  const filteredBirthdays = useMemo(
    () => filterByCalendarPeopleGroups(birthdays, peopleFilter, availablePeopleGroups),
    [birthdays, peopleFilter, availablePeopleGroups]
  );
  const filteredAnniversaries = useMemo(
    () => filterByCalendarPeopleGroups(anniversaries, peopleFilter, availablePeopleGroups),
    [anniversaries, peopleFilter, availablePeopleGroups]
  );

  const visible = useMemo(() => calendarWindow(view, weekStartsOn), [view, weekStartsOn]);
  const todayWindow = useMemo(() => calendarWindow({kind: 'ahead'}, weekStartsOn), [weekStartsOn]);

  const {days, overlapLayers} = useMemo(
    () => buildCalendarDays(visible, filteredPeople, filteredBirthdays, filteredAnniversaries),
    [visible, filteredPeople, filteredBirthdays, filteredAnniversaries]
  );

  // Once data lands, if nobody is out in the `ahead` grid, open to the next
  // month that has someone out (e.g. skip ahead to August).
  useEffect(() => {
    if (didAutoJump || isLoading) {
      return;
    }
    if (view.kind === 'ahead') {
      const outAhead = peopleOutInRange(visible.gridStart, visible.gridEnd, filteredPeople);
      const target =
        outAhead.length === 0 ? firstUpcomingOutMonth(filteredPeople, new Date()) : null;
      if (target) {
        setView({kind: 'month', month: target});
      }
    }
    setDidAutoJump(true);
  }, [didAutoJump, isLoading, filteredPeople, view, visible]);

  // From `ahead`, › opens next month and ‹ the current month — unless `ahead` is
  // already the current month's grid, then it's a plain step.
  const stepMonth = useCallback(
    (delta: 1 | -1) => {
      setDidAutoJump(true);
      setView((prev) => {
        if (prev.kind === 'month') {
          return {kind: 'month', month: addMonths(prev.month, delta)};
        }
        const ahead = calendarWindow(prev, weekStartsOn);
        if (ahead.month) {
          return {kind: 'month', month: addMonths(ahead.month, delta)};
        }
        return {kind: 'month', month: delta === 1 ? addMonths(currentMonth, 1) : currentMonth};
      });
    },
    [currentMonth, weekStartsOn]
  );
  const goToPrevMonth = useCallback(() => stepMonth(-1), [stepMonth]);
  const goToNextMonth = useCallback(() => stepMonth(1), [stepMonth]);
  const goToToday = useCallback(() => {
    setDidAutoJump(true);
    setView({kind: 'ahead'});
  }, []);

  return {
    visible,
    label: calendarWindowLabel(visible),
    days,
    overlapLayers,
    weekStartsOn,
    people: filteredPeople,
    birthdays: filteredBirthdays,
    anniversaries: filteredAnniversaries,
    isLoading,
    goToPrevMonth,
    goToNextMonth,
    goToToday,
    isOnToday:
      isSameDay(visible.gridStart, todayWindow.gridStart) &&
      visible.month?.getTime() === todayWindow.month?.getTime(),
    availablePeopleGroups,
  };
};
